import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, type LucideIcon } from 'lucide-react';

export interface StatTileProps {
  label: string;
  value: ReactNode;
  icon: LucideIcon;
  /** The icon badge's fill, e.g. `bg-blue-500`. */
  color: string;
  subtext?: string;
  /**
   * Where the number's own records are listed — a count of events opens the
   * events list filtered to those events. A tile with nothing behind it (a
   * capacity, a total of money) has none and is not a link.
   */
  to?: string;
  /** `compact`: icon first, small figure (a record page). `large`: the dashboard's. */
  size?: 'compact' | 'large';
}

/** One aggregate figure; when it counts records, a link to the list of them. */
export function StatTile({
  label,
  value,
  icon: Icon,
  color,
  subtext,
  to,
  size = 'compact',
}: StatTileProps) {
  const large = size === 'large';
  const badge = (
    <div
      className={`${large ? 'w-12 h-12' : 'w-10 h-10'} shrink-0 rounded-lg flex items-center justify-center ${color}`}
    >
      <Icon size={large ? 22 : 18} className="text-white" />
    </div>
  );
  const text = (
    <div className="min-w-0 flex-1">
      <p className={large ? 'text-sm text-gray-500' : 'text-xs text-gray-500'}>{label}</p>
      <p className={`font-bold text-gray-900 ${large ? 'text-2xl mt-1' : 'text-lg'}`}>{value}</p>
      {subtext && <p className="text-xs text-gray-400 mt-1">{subtext}</p>}
    </div>
  );
  const body = large ? (
    <>
      {text}
      {badge}
    </>
  ) : (
    <>
      {badge}
      {text}
      {to && <ChevronRight size={16} className="text-gray-300 shrink-0" aria-hidden />}
    </>
  );
  const frame = `bg-white rounded-xl border flex items-center gap-3 ${
    large ? 'shadow-sm border-gray-100 p-5 justify-between' : 'border-gray-200 p-4'
  }`;

  if (!to) return <div className={frame}>{body}</div>;
  return (
    <Link
      to={to}
      className={`${frame} hover:border-primary-300 hover:shadow-md transition focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500`}
    >
      {body}
    </Link>
  );
}
